// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import { ScrollOrientation } from '@epicuro-next/ataraxia/scroll-buttons';
import {
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
  IDynamicItem,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import {
  createInstance,
  createInstanceWithChildren,
  getProvider,
} from '../../utils/dynamic-items-utils';

import { TypeForItemMediaPreviewInput } from './item-media-preview';
import { getProductAsLoadableImageView } from '../views/product-as-image';

// The reason to have this separate as if another component is added that requires other data, then
// it is more clear how the type below is constructed from several types.
export type ScrollableContainerSingleProductDataType =
  TypeForItemMediaPreviewInput;
export type ScrollableContainerProductDataType =
  ScrollableContainerSingleProductDataType[];

export type ScrollableContainerDataProviders = {
  isLoading: IDynamicBindableValue | IDynamicBindableValueCombination;
  hasError?: IDynamicBindableValue;
};

export function getScrollableContainer(
  providers: ScrollableContainerDataProviders,
  orientation: ScrollOrientation = ScrollOrientation.horizontal,
) {
  const itemView = getProductAsLoadableImageView();

  const flexDirection: 'flex-row' | 'flex-column' =
    orientation === ScrollOrientation.horizontal ? 'flex-row' : 'flex-column';

  const loadingSkeletonsArrayRenderer = createInstance({
    id: 'loading-skeletons-array-renderer',
    contentTypeId: 'dynamic-elements-array-renderer',
    if: ['EQ', [providers.isLoading, true]],
    styles: {
      classes: [
        'panel-skeletons-wrapper',
        'd-flex',
        flexDirection,
        'flex-shrink-0',
      ],
    },
    data: {
      item: [{}],
    },
    inputs: {
      propertyToUse: 'notApplicable',
      contentType: itemView.loadingSkeletonContentType,
      items$: {
        provider: 'self',
        property: 'item',
        transform: function (item: {}) {
          const nrOfSkeletons = 20;
          return Array(nrOfSkeletons).fill(item);
        }.toString(),
      },
    },
  });

  const scrollableContainer = createInstanceWithChildren({
    id: 'scrollable-container',
    contentTypeId: 'dynamic-elements-scrollable-container',
    styles: {
      classes: ['d-block', 'h-100'],
    } as IDynamicItem['styles'],
    if: undefined as IDynamicItem['if'],
    data: {
      itemData: {
        provider: 'id:root',
        property: 'productData',
      },
      isLoading: providers.isLoading,
    },
    inputs: {
      showScrollButtons: true,
      horizontalPadding: '0px',
      wheelHorizontalScrollEnabled: false,
      scrollOrientation: orientation,
    },
    children: [],
  });

  if (providers.hasError) {
    scrollableContainer.if = ['EQ', [providers.hasError, false]];
  }

  const itemsArrayRenderer = createInstance({
    id: 'items-array-renderer',
    contentTypeId: 'dynamic-elements-array-renderer',
    styles: {
      classes: [
        'panel-items-wrapper',
        'd-flex',
        flexDirection,
        'flex-shrink-0',
      ],
      style: {
        __multiple__: {
          provider: 'id:root',
          property: 'arrayRendererStyle',
        },
      },
    },
    data: {
      itemData: getProvider(scrollableContainer, 'itemData'),
    },
    inputs: {
      items$: {
        provider: 'self',
        property: 'itemData',
      },
      propertyToUse: 'productData',
      contentType: itemView.itemContentType,
    },
  });

  scrollableContainer.children.push(
    ...[loadingSkeletonsArrayRenderer, itemsArrayRenderer],
  );

  return {
    scrollableContainer,
    itemsArrayRenderer,
    loadingSkeletonsArrayRenderer,
  };
}
